<template>
	<div class="business-page">
		<!-- ===== Hero Banner (aligned with About.vue) ===== -->
		<div class="hero-banner">
			<div class="hero-container">
				<div class="hero-content">
					<h1>{{ $t('contactInfo.title') }}</h1>
					<div class="hero-divider"></div>
					<p class="hero-subtitle">{{ $t('businessPage.heroSubtitle') }}</p>
				</div>
			</div>
		</div>

		<!-- ===== Quick Contact Strip ===== -->
		<section class="quick-strip">
			<div class="strip-inner">
				<div class="strip-item">
					<div class="strip-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
							<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
							<circle cx="12" cy="10" r="3"/>
						</svg>
					</div>
					<div>
						<div class="strip-label">{{ $t('businessPage.companyAddressLabel') }}</div>
						<div class="strip-value-sm">{{ $t('contactInfo.companyAddress') }}</div>
					</div>
				</div>
				<div class="strip-item">
					<div class="strip-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
							<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
						</svg>
					</div>
					<div>
						<div class="strip-label">{{ $t('businessPage.phoneLabel') }}</div>
						<div class="strip-value">{{ $t('businessPage.primaryPhone') }}</div>
						<div class="strip-value-sm">{{ $t('businessPage.landlinePhone') }}</div>
					</div>
				</div>
				<div class="strip-item">
					<div class="strip-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
							<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
							<polyline points="22,6 12,13 2,6"/>
						</svg>
					</div>
					<div>
						<div class="strip-label">{{ $t('businessPage.emailLabel') }}</div>
						<div class="strip-value-sm">{{ $t('businessPage.contactEmail') }}</div>
					</div>
				</div>
			</div>
		</section>

		<!-- ===== Map Section ===== -->
		<section class="map-section">
			<div class="map-full">
				<div ref="mapContainer" class="map-full-inner"></div>

				<!-- Map Load Failure Fallback -->
				<div v-if="mapLoadFailed" class="map-fallback">
					<div class="map-fallback-inner">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="48" height="48">
							<circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
						</svg>
						<p>{{ $t('businessPage.mapUnavailable') }}</p>
						<span>{{ mapErrorMsg }}</span>
						<button class="map-retry-btn" @click="initAmap">{{ $t('businessPage.mapReload') }}</button>
					</div>
				</div>

				<!-- Map Buttons -->
				<div class="map-btns">
					<button class="map-btn" @click="openMap">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="16" height="16">
							<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
						</svg>
						{{ $t('businessPage.viewInGoogleMaps') }}
					</button>
					<button class="map-btn" @click="openAmap">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="16" height="16">
							<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
						</svg>
						{{ $t('businessPage.viewInAmap') }}
					</button>
				</div>
			</div>
		</section>

		<!-- ===== Global Offices ===== -->
		<section class="offices-section">
			<div class="offices-header">
				<h2 class="offices-title">{{ $t('contactInfo.globalOffices') }}</h2>
				<p class="offices-desc">{{ $t('businessPage.officesDescription') }}</p>
			</div>
			<div class="offices-grid">
				<div
					v-for="office in $t('contactInfo.offices')"
					:key="office.id"
					class="office-card"
					:class="{ active: activeOffice === office.id }"
					@mouseenter="activeOffice = office.id"
					@mouseleave="activeOffice = null"
					@touchstart="handleOfficeTouch(office.id)"
				>
					<div class="office-photo">
						<img :src="getCityPhoto(office.id)" :alt="office.city" loading="lazy" />
					</div>
					<div class="office-card-body">
						<div class="office-city-row">
							<svg class="office-city-pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
							</svg>
							<span class="office-city-name">{{ office.city }}</span>
							<span class="office-tag" v-if="getOfficeTag(office.id)">{{ $t('businessPage.' + getOfficeTag(office.id)) }}</span>
						</div>
						<div class="office-address">{{ office.address }}</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader';

	export default {
		name: 'BusinessPage',
		data() {
			return {
				activeOffice: null,
				touchTimer: null,
				amapInstance: null,
				mapLoadFailed: false,
				mapErrorMsg: '',
			};
		},
		mounted() {
			this.initAmap();
		},
		beforeDestroy() {
			if (this.amapInstance) {
				this.amapInstance.destroy();
				this.amapInstance = null;
			}
		},
		methods: {
			initAmap() {
				// 高德地图 2.0 安全密钥配置
				window._AMapSecurityConfig = {
					securityJsCode: '1a737104fcd7864ae623a68d8ef8afc7',
				};
				AMapLoader.load({
					key: 'ef94599e4516e442106a6fbe11d9be06',
					version: '2.0',
					plugins: ['AMap.Marker', 'AMap.InfoWindow'],
				}).then((AMap) => {
					const map = new AMap.Map(this.$refs.mapContainer, {
						center: [116.608711, 40.11436],
						zoom: 15,
						resizeEnable: true,
					});
					this.amapInstance = map;

					const marker = new AMap.Marker({
						position: [116.608711, 40.11436],
						title: this.$t('map.title'),
					});
					map.add(marker);

					const infoWindow = new AMap.InfoWindow({
						content: this.$t('map.title'),
						offset: new AMap.Pixel(0, -30),
					});
					infoWindow.open(map, [116.608711, 40.11436]);

					marker.on('click', () => {
						infoWindow.open(map, [116.608711, 40.11436]);
					});
				}).catch((err) => {
					console.error('高德地图加载失败:', err);
					this.mapLoadFailed = true;
					if (err && err.info === 'USER_DAILY_QUERY_OVER_LIMIT') {
						this.mapErrorMsg = this.$t('businessPage.mapErrorRateLimit');
					} else {
						this.mapErrorMsg = this.$t('businessPage.mapErrorUnavailable');
					}
				});
			},
			openMap() {
				window.open('https://maps.app.goo.gl/hyrdyWcNwJRcjMuf9');
			},
			openAmap() {
				window.open('https://uri.amap.com/search?keyword=' + this.$t('businessPage.amapSearchKeyword') + '');
			},
			handleOfficeTouch(officeId) {
				if (this.touchTimer) clearTimeout(this.touchTimer);
				this.activeOffice = officeId;
				this.touchTimer = setTimeout(() => {
					this.activeOffice = null;
				}, 2000);
			},
			getCityPhoto(id) {
				const photos = {
					1: require('@/assets/images/beijing-北京.png'),
					2: require('@/assets/images/sh.png'),
					3: require('@/assets/images/gz.png'),
					4: require('@/assets/images/zhengzhou-zz.png'),
					5: require('@/assets/images/cd1.png'),
					6: require('@/assets/images/cq1.png'),
					7: require('@/assets/images/ezhou-ez.png'),
				};
				return photos[id] || photos[1];
			},
			getOfficeTag(id) {
				const tags = {
					1: 'officeTagHeadquarters',
					2: 'officeTagEastChina',
					3: 'officeTagSouthChina',
					4: 'officeTagCentralChina',
					5: 'officeTagSouthwestChina',
					6: 'officeTagSouthwestChina',
					7: 'officeTagCentralChina',
				};
				return tags[id] || '';
			}
		}
	};
</script>

<style lang="scss" scoped>
	// === Design System Variables ===
	$primary: #0052CC;
	$primary-dark: #0043A8;
	$primary-light: #3B82F6;
	$accent: #2563EB;
	$navy: #0A1628;
	$navy-mid: #0D2B4E;
	$heading: #0F172A;
	$body: #475569;
	$muted: #94A3B8;
	$border: #E2E8F0;
	$light-bg: #F8FAFC;
	$white: #ffffff;
	$radius: 16px;
	$radius-sm: 10px;

	.business-page {
		font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
		color: $body;
		line-height: 1.6;
		background: $light-bg;
		-webkit-font-smoothing: antialiased;
	}

	/* ====== Hero Banner (aligned with About.vue) ====== */
	.hero-banner {
		width: 100%;
		height: 60vh;
		min-height: 400px;
		position: relative;
		border-radius: 20px;
		overflow: hidden;
		background: linear-gradient(135deg, rgba(10, 22, 40, .75) 0%, rgba(0, 82, 204, .3) 100%),
			url('../assets/images/zjwm.png') center/cover no-repeat;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 4px 20px rgba(0, 60, 120, 0.1);

		.hero-container {
			max-width: 1200px;
			width: 90%;
			margin: 0 auto;
			padding: 40px;
			background-color: rgba(255, 255, 255, 0.2);
			backdrop-filter: blur(8px);
			border-radius: 12px;
			border: 1px solid rgba(255, 255, 255, 0.3);
			box-shadow: 0 8px 32px rgba(0, 60, 120, 0.1);
		}

		.hero-content {
			max-width: 800px;
			text-align: center;
			animation: fadeInUp 1.2s ease-out;

			h1 {
				font-size: 2.6rem;
				font-weight: 800;
				margin-bottom: 1.5rem;
				color: #fff;
				text-shadow: 0 2px 12px rgba(0, 60, 120, 0.6);
				letter-spacing: -0.5px;
				line-height: 1.2;
				position: relative;
				display: inline-block;

				&::after {
					content: '';
					display: block;
					width: 80px;
					height: 4px;
					background: rgba(255, 255, 255, 0.7);
					margin: 20px auto;
					border-radius: 2px;
				}
			}

			.hero-divider {
				width: 60px;
				height: 3px;
				background: $primary-light;
				border-radius: 2px;
				margin: 0 auto 20px;
				display: none;
			}

			.hero-subtitle {
				font-size: clamp(1.1rem, 2vw, 1.2rem);
				color: rgba(255, 255, 255, 0.85);
				// max-width: 650px;
				margin: 0 auto;
				line-height: 1.7;
			}
		}

		@media (max-width: 1200px) {
			height: 60vh;
			min-height: 500px;
		}

		@media (max-width: 1100px) {
			height: 55vh;
			min-height: 450px;
		}

		@media (max-width: 768px) {
			height: 50vh;
			min-height: 400px;

			.hero-container {
				padding: 0 20px;
			}

			.hero-content {
				h1 { font-size: 2rem; }
				.hero-subtitle { font-size: 1rem; }
			}
		}

		@media (max-width: 480px) {
			height: 45vh;
			min-height: 350px;

			.hero-content h1 { font-size: 1.8rem; }
		}
	}

	/* ====== Quick Contact Strip ====== */
	.quick-strip {
		position: relative;
		z-index: 10;
		margin-top: -40px;
		padding: 0 48px;

		@media (max-width: 768px) {
			padding: 0 20px;
			margin-top: -30px;
		}
	}

	.strip-inner {
		max-width: 1200px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 0;
		background: $white;
		border-radius: $radius;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
		overflow: hidden;

		@media (max-width: 1024px) {
			grid-template-columns: repeat(2, 1fr);
		}

		@media (max-width: 768px) {
			grid-template-columns: 1fr;
		}
	}

	.strip-item {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 28px;
		position: relative;
		transition: background 0.3s;

		&:not(:last-child)::after {
			content: '';
			position: absolute;
			right: 0;
			top: 20%;
			height: 60%;
			width: 1px;
			background: $border;
		}

		&:hover {
			background: #F1F5F9;
		}

		@media (max-width: 1024px) {
			&:nth-child(2)::after {
				display: none;
			}
		}

		@media (max-width: 768px) {
			&::after {
				display: none;
			}
			border-bottom: 1px solid $border;
		}
	}

	.strip-icon {
		width: 48px;
		height: 48px;
		border-radius: 12px;
		background: linear-gradient(135deg, $primary, $accent);
		display: flex;
		align-items: center;
		justify-content: center;
		color: #fff;
		flex-shrink: 0;

		svg {
			width: 22px;
			height: 22px;
		}
	}

	.strip-label {
		font-size: 0.78rem;
		color: $muted;
		font-weight: 500;
		margin-bottom: 2px;
	}

	.strip-value {
		font-size: 0.95rem;
		color: $heading;
		font-weight: 700;
	}

	.strip-value-sm {
		font-size: 0.82rem;
		color: $body;
		font-weight: 500;
	}

	/* ====== Map Section ====== */
	.map-section {
		position: relative;
		margin-top: 80px;

		@media (max-width: 768px) {
			margin-top: 50px;
		}
	}

	.map-full {
		width: 100%;
		height: 560px;
		position: relative;
		overflow: hidden;

		@media (max-width: 768px) {
			height: 480px;
		}
	}

	.map-full-inner {
		width: 100%;
		height: 100%;
	}

	/* Map Fallback */
	.map-fallback {
		position: absolute;
		inset: 0;
		z-index: 5;
		background: rgba(15, 23, 42, 0.92);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.map-fallback-inner {
		text-align: center;
		color: #fff;

		svg {
			color: #F59E0B;
			margin-bottom: 12px;
		}

		p {
			font-size: 1.1rem;
			font-weight: 600;
			margin: 0 0 6px;
		}

		span {
			font-size: 0.82rem;
			color: rgba(255, 255, 255, 0.55);
		}
	}

	.map-retry-btn {
		display: block;
		margin: 16px auto 0;
		padding: 8px 24px;
		background: $primary;
		color: #fff;
		border: none;
		border-radius: 8px;
		font-size: 0.85rem;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.3s;

		&:hover {
			background: $primary-dark;
		}
	}

	/* Floating Cards */
	.float-cards {
		position: absolute;
		top: 50%;
		left: 48px;
		transform: translateY(-50%);
		z-index: 10;
		display: flex;
		flex-direction: column;
		gap: 16px;
		max-width: 380px;

		@media (max-width: 1024px) {
			max-width: 320px;
			left: 24px;
		}

		@media (max-width: 768px) {
			position: relative;
			top: auto;
			left: auto;
			transform: none;
			max-width: 100%;
			padding: 24px 20px;
			flex-direction: row;
			flex-wrap: wrap;
		}
	}

	.float-card {
		background: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(16px);
		border-radius: 14px;
		padding: 24px 28px;
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
		border: 1px solid rgba(255, 255, 255, 0.8);
		transition: transform 0.3s, box-shadow 0.3s;

		&:hover {
			transform: translateY(-4px);
			box-shadow: 0 16px 48px rgba(0, 0, 0, 0.16);
		}

		@media (max-width: 768px) {
			flex: 1;
			min-width: 260px;
		}
	}

	.float-card-head {
		display: flex;
		align-items: center;
		gap: 14px;
		margin-bottom: 14px;
	}

	.float-card-dot {
		width: 10px;
		height: 10px;
		border-radius: 50%;
		flex-shrink: 0;

		&.blue { background: $primary; }
		&.green { background: #10B981; }
		&.amber { background: #F59E0B; }
	}

	.float-card-title {
		font-size: 1rem;
		font-weight: 700;
		color: $heading;
	}

	.float-card {
		p {
			font-size: 0.88rem;
			color: $body;
			line-height: 1.7;
			margin: 0;
		}

		.big-num {
			font-size: 1.4rem;
			font-weight: 800;
			color: $primary;
			letter-spacing: 0.5px;
			margin: 4px 0;
		}
	}

	.float-card-link {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 0.82rem;
		font-weight: 600;
		color: $primary;
		margin-top: 10px;
		transition: gap 0.3s;
		text-decoration: none;

		&:hover {
			gap: 10px;
		}
	}

	/* Map Buttons */
	.map-btns {
		position: absolute;
		bottom: 20px;
		right: 20px;
		z-index: 10;
		display: flex;
		gap: 10px;

		@media (max-width: 600px) {
			position: relative;
			bottom: auto;
			right: auto;
			padding: 16px 20px;
			justify-content: center;
		}
	}

	.map-btn {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 10px 20px;
		background: rgba(255, 255, 255, 0.92);
		backdrop-filter: blur(8px);
		border: 1px solid rgba(255, 255, 255, 0.8);
		border-radius: $radius-sm;
		font-size: 0.85rem;
		font-weight: 600;
		color: $heading;
		cursor: pointer;
		transition: all 0.3s;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

		&:hover {
			background: $primary;
			color: #fff;
		}
	}

	/* ====== Offices Section ====== */
	.offices-section {
		padding: 100px 48px;
		background: $white;

		@media (max-width: 768px) {
			padding: 60px 20px;
		}
	}

	.offices-header {
		max-width: 1200px;
		margin: 0 auto 50px;
		text-align: left;
	}

	.offices-title {
		font-size: 2.2rem;
		font-weight: 800;
		color: $heading;
		letter-spacing: -0.5px;
		margin: 0 0 12px;

		@media (max-width: 768px) {
			font-size: 1.6rem;
		}
	}

	.offices-desc {
		font-size: 1rem;
		color: $body;
		max-width: 520px;
		margin: 0;
	}

	.offices-grid {
		max-width: 1200px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 24px;

		@media (max-width: 1024px) {
			grid-template-columns: repeat(2, 1fr);
		}

		@media (max-width: 600px) {
			grid-template-columns: 1fr;
		}
	}

	.office-card {
		background: $white;
		border-radius: $radius;
		overflow: hidden;
		border: 1px solid $border;
		transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
		cursor: pointer;
		position: relative;

		&:hover,
		&.active {
			border-color: $primary;
			box-shadow: 0 12px 40px rgba(0, 82, 204, 0.12);
			transform: translateY(-6px);
		}

		&:hover .office-photo img,
		&.active .office-photo img {
			transform: scale(1.06);
		}
	}

	.office-photo {
		height: 200px;
		position: relative;
		overflow: hidden;
		background: $light-bg;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
			transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
		}

		@media (max-width: 600px) {
			height: 170px;
		}
	}

	.office-city-row {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		margin-bottom: 10px;
	}

	.office-city-pin {
		width: 16px;
		height: 16px;
		color: $primary;
		flex-shrink: 0;
	}

	.office-city-name {
		font-size: 1.05rem;
		font-weight: 700;
		color: $heading;
		letter-spacing: 0.5px;
	}

	.office-card-body {
		padding: 20px 24px 24px;
	}

	.office-address {
		font-size: 0.88rem;
		color: $body;
		line-height: 1.7;
	}

	.office-tag {
		display: inline-block;
		margin-left: 4px;
		font-size: 0.72rem;
		font-weight: 600;
		color: $primary;
		background: rgba(0, 82, 204, 0.06);
		padding: 3px 10px;
		border-radius: 20px;
		letter-spacing: 0.5px;
		white-space: nowrap;
	}
</style>
